// Per-store adapters for the locale copy in ./localeSync.ts: what's translatable, how to label it, how to write it back

import type { SyncSource, PendingWrite } from "./localeSync"

import * as kb from "../articles/store"
import { type KBStore, nodeTitle, type Translation } from "../articles/types"

import * as svc from "../service-map/store"
import {
  type ServiceStore, type EntityKind, BUCKET as SVC_BUCKET, displayName,
} from "../service-map/types"

import * as sc from "../site-config/store"
import {
  type SiteConfigStore, SITECONFIG_BUCKET, localesInSiteConfigStore,
} from "../site-config/types"

export type SectionKey = "articles" | "serviceMap" | "siteConfig"

export const SECTION_LABELS: Record<SectionKey, string> = {
  articles: "Articles",
  serviceMap: "Service Map",
  siteConfig: "Site Config",
}

export interface StoreAdapter<S> {
  section: SectionKey
  sources: (store: S) => SyncSource[]
  locales: (store: S) => string[] // default first
  defaultLocale: (store: S) => string
  // No-op for site config, whose locale list is derived from content
  ensureLocale: (store: S, locale: string) => S
  // Writes through the store's own bulk path so history and sync behave like a hand edit
  apply: (
    store: S,
    writes: PendingWrite[],
    locale: string,
    onBefore?: (bucket: string, id: string, before: any) => void,
  ) => { store: S; applied: number }
}

export const BUCKET_LABELS: Record<string, string> = {
  categories: "Categories",
  sections: "Sections",
  articles: "Articles",
  countries: "Countries",
  regions: "Regions",
  cities: "Cities",
  providerCategories: "Provider categories",
  serviceCategories: "Service categories",
  tags: "Tags",
  populations: "Populations",
  accessibility: "Accessibility",
  providers: "Providers",
  services: "Services",
  pageContents: "Page content",
}

// ── Articles ─────────────────────────────────────────────────────────────────

export const articlesAdapter: StoreAdapter<KBStore> = {
  section: "articles",
  sources: (store) => {
    const out: SyncSource[] = []
    for (const bucket of ["categories", "sections", "articles"] as const) {
      for (const node of Object.values(store[bucket])) {
        out.push({
          bucket,
          id: node.id,
          name: nodeTitle(node, store.defaultLocale, store.defaultLocale),
          record: node,
        })
      }
    }
    return out
  },
  locales: (store) => {
    const rest = store.locales.filter(l => l !== store.defaultLocale).sort()
    return [store.defaultLocale, ...rest]
  },
  defaultLocale: (store) => store.defaultLocale,
  ensureLocale: (store, locale) => kb.ensureLocale(store, locale),
  apply: (store, writes, locale, onBefore) => {
    const { store: next, applied } = kb.setTranslationsBulk(
      store,
      writes.map(w => ({
        id: w.id,
        locale,
        // Fill required Translation fields the source may lack
        translation: {
          title: "", body: "", description: "", reviewed: false,
          ...w.translation,
          locale,
        } as Translation,
      })),
      (w, before) => onBefore?.(bucketOfNode(store, w.id), w.id, before),
    )
    return { store: next, applied }
  },
}

function bucketOfNode(store: KBStore, id: string): string {
  if (store.categories[id]) return "categories"
  if (store.sections[id]) return "sections"
  return "articles"
}

// ── Service map ──────────────────────────────────────────────────────────────

// Translatable buckets and the EntityKind each writes through
const SVC_TRANSLATABLE: Array<[keyof ServiceStore, EntityKind]> = [
  ["countries", "country"],
  ["regions", "region"],
  ["cities", "city"],
  ["providerCategories", "providerCategory"],
  ["serviceCategories", "serviceCategory"],
  ["tags", "tag"],
  ["populations", "population"],
  ["accessibility", "accessibility"],
  ["providers", "provider"],
  ["services", "service"],
]

export const SVC_KIND_OF_BUCKET = new Map<string, EntityKind>(
  SVC_TRANSLATABLE.map(([bucket, kind]) => [bucket as string, kind]),
)

export const serviceMapAdapter: StoreAdapter<ServiceStore> = {
  section: "serviceMap",
  sources: (store) => {
    const out: SyncSource[] = []
    for (const [bucket] of SVC_TRANSLATABLE) {
      for (const entity of Object.values(store[bucket] as Record<string, any>)) {
        out.push({
          bucket: bucket as string,
          id: entity.id,
          name: displayName(entity, store.defaultLocale, store.defaultLocale),
          record: entity,
        })
      }
    }
    return out
  },
  locales: (store) => {
    const codes = Object.keys(store.languages)
    const rest = codes.filter(c => c !== store.defaultLocale).sort()
    return [store.defaultLocale, ...rest]
  },
  defaultLocale: (store) => store.defaultLocale,
  ensureLocale: (store, locale) =>
    store.languages[locale]
      ? store
      : svc.upsertLanguage(store, { code: locale, name: locale, direction: "ltr" }),
  apply: (store, writes, locale, onBefore) => {
    const { store: next, applied } = svc.setTranslationsBulk(
      store,
      writes.flatMap(w => {
        const kind = SVC_KIND_OF_BUCKET.get(w.bucket)
        if (!kind) return []
        return [{
          kind, id: w.id, locale,
          patch: {
            name: w.translation.name ?? "",
            description: w.translation.description,
            address: w.translation.address,
            definition: w.translation.definition,
            reviewed: false,
            status: w.translation.status,
          },
        }]
      }),
      (w, before) => onBefore?.(SVC_BUCKET[w.kind] as string, w.id, before),
    )
    return { store: next, applied }
  },
}

// ── Site config ──────────────────────────────────────────────────────────────

export const siteConfigAdapter: StoreAdapter<SiteConfigStore> = {
  section: "siteConfig",
  sources: (store) => {
    // Only pageContent has row translations; header/footer inline locale maps aren't copied
    return Object.values(store.pageContents).map(pc => ({
      bucket: "pageContents",
      id: pc.id,
      name: sc.displayName(store, "pageContent", pc.id, store.defaultLocale),
      record: pc,
    }))
  },
  // Derived from content; site config has no locale registry
  locales: (store) => localesInSiteConfigStore(store),
  defaultLocale: (store) => store.defaultLocale,
  ensureLocale: (store) => store,
  apply: (store, writes, locale, onBefore) => {
    const { store: next, applied } = sc.setTranslationsBulk(
      store,
      writes.map(w => ({
        kind: "pageContent" as const,
        id: w.id,
        locale,
        translation: { ...w.translation, reviewed: false },
      })),
      (w, before) => onBefore?.(SITECONFIG_BUCKET[w.kind] as string, w.id, before),
    )
    return { store: next, applied }
  },
}
